import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ShippingStatus } from "../types";
import LabeledTextField from "./LabeledTextField";
import LabeledSelect from "./LabeledSelect";
import { shippingStatusList } from "../constants";
import LabeledComponent from "./LabeledComponent";
import { checkBusinessDate } from "../utils/common";
import DateOrTextField from "./DateOrTextField";

type Props = {
  open: boolean;
  onClose: Function;
  onUpdate: Function;
  shippingCompanyName: string;
  trackingNumber: string;
  shippingDate: string;
  expectedShippingDate: string;
  shippingStatus: ShippingStatus;
  arrivalDate: string;
  setShippingCompanyName: Dispatch<SetStateAction<string>>;
  setTrackingNumber: Dispatch<SetStateAction<string>>;
  setShippingDate: Dispatch<SetStateAction<string>>;
  setExpectedShippingDate: Dispatch<SetStateAction<string>>;
  setShippingStatus: Dispatch<SetStateAction<ShippingStatus>>;
  setArrivalDate: Dispatch<SetStateAction<string>>;
  setExpectedShippingDateError: Dispatch<SetStateAction<string | null>>;
  setShippingDateError: Dispatch<SetStateAction<string | null>>;
};

/**
 * 出荷情報設定モーダル
 * @param root0 コンポーネントのプロップス
 */
export default function ShipInfo({
  open,
  onClose,
  onUpdate,
  shippingCompanyName,
  trackingNumber,
  shippingDate,
  expectedShippingDate,
  shippingStatus,
  arrivalDate,
  setShippingCompanyName,
  setTrackingNumber,
  setShippingDate,
  setExpectedShippingDate,
  setShippingStatus,
  setArrivalDate,
  setExpectedShippingDateError,
  setShippingDateError,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      PaperProps={{
        sx: {
          minWidth: 550,
          // width: '80vw',
          maxWidth: 1200,
        },
      }}
    >
      <DialogTitle>出荷情報設定</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          {/* <Box> */}
          {/* タイトル */}
          <Grid item>
            <Typography ml={2} color="rgb(50,50,50)">
              選択した注文の出荷情報をまとめて設定します。<br />出荷数は次の出荷確認画面で変更可能です。
            </Typography>
          </Grid>
          {/* フォーム */}
          <Grid item container justifyContent="center">
            <Grid item textAlign="center" justifyItems="center" my={3} mr={5}>
              {/* <Box textAlign="center" justifyItems="center" my={3}> */}
              <LabeledTextField
                id="shipping_company_name"
                type="text"
                label="配送社情報"
                text={shippingCompanyName}
                setText={setShippingCompanyName}
                // textRequired
              />
              <LabeledTextField
                id="tracking_number"
                type="text"
                label="送り状番号"
                text={trackingNumber}
                setText={setTrackingNumber}
                maxLength={50}
              />
              <LabeledComponent label="仕入先出荷日">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                  <DatePicker
                    label="仕入先出荷日"
                    format="YYYY/MM/DD"
                    minDate={dayjs()}
                    value={shippingDate ? dayjs(shippingDate, "YYYY/MM/DD") : null}
                    onChange={(val) => setShippingDate(val ? val.format("YYYY/MM/DD") : "")}
                    slotProps={{ textField: { size: "small" } }}
                    sx={{
                      width: "207px",
                    }}
                    shouldDisableDate={(date) => {
                      if (!checkBusinessDate(date)) {
                        return true; // 営業日でない場合は選択不可
                      }
                      return false;
                    }}
                    onError={(reason) => {
                      setShippingDateError(reason ? "出荷予定日の日付が無効です。" : null);
                    }}
                  />
                </LocalizationProvider>
              </LabeledComponent>
              <LabeledComponent label="仕入先出荷予定日">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                  <DatePicker
                    label="出荷予定日"
                    format="YYYY/MM/DD"
                    minDate={dayjs()}
                    value={expectedShippingDate ? dayjs(expectedShippingDate, "YYYY/MM/DD") : null}
                    onChange={(val) => setExpectedShippingDate(val ? val.format("YYYY/MM/DD") : "")}
                    slotProps={{ textField: { size: "small" } }}
                    sx={{
                      width: "207px",
                    }}
                    shouldDisableDate={(date) => {
                      if (!checkBusinessDate(date)) {
                        return true; // 営業日でない場合は選択不可
                      }
                      return false;
                    }}
                    onError={(reason) => {
                      setExpectedShippingDateError(reason ? "出荷予定日の日付が無効です。" : null);
                    }}
                  />
                </LocalizationProvider>
              </LabeledComponent>
              <LabeledSelect
                id="tracking_number"
                label="出荷/入荷待ち/欠品"
                value={shippingStatus}
                onChange={(val: ShippingStatus) => setShippingStatus(val)}
                options={shippingStatusList}
                textRequired
              />
              <LabeledComponent label="仕入先入荷予定">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                  <Box p={0} maxWidth={360}>
                    <DateOrTextField
                      label="仕入先入荷予定"
                      value={arrivalDate}
                      onChange={setArrivalDate}
                    />
                  </Box>
                </LocalizationProvider>
              </LabeledComponent>
            </Grid>
            {/* </Box> */}
          </Grid>
        </Grid>
        {/* </Box> */}
      </DialogContent>
      <DialogActions>
        <Button variant="custom-outlined" onClick={() => onClose()}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={() => onUpdate()}>
          出荷確認
        </Button>
      </DialogActions>
    </Dialog>
  );
}
